import { apiClient, API_PRX } from '@indipia/shared';

const BASE = API_PRX.MEMBER;

// ── 타입 ──────────────────────────────────────────────────────────────────────

export interface UserProfile {
  memberId: number;
  loginId: string;
  email: string | null;
  memberName: string;
  mobile: string | null;
  masterYn: 'Y' | 'N';
  snsYn: 'Y' | 'N';
  snsType: string | null;
  profileImgUrl: string | null;
  // 업체 정보 (마스터만)
  compId: number | null;
  compNo: string | null;
  compType: string | null;
  compName: string | null;
  compRepName: string | null;
  compPhone: string | null;
  compEmail: string | null;
  compAddress: string | null;
  compAddressDtl: string | null;
}

export interface WithdrawalCheck {
  canWithdraw: boolean;
  reason: string | null;
  activeEmployeeCount: number;
}

// ── 프로필 조회 ───────────────────────────────────────────────────────────────

export async function fetchProfile(): Promise<UserProfile> {
  const res = await apiClient.get(`${BASE}/users/me`);
  return res.data.data as UserProfile;
}

// ── 개인정보 수정 (multipart/form-data) ──────────────────────────────────────

export async function updatePersonalInfo(params: {
  memberName?: string;
  mobile?: string;
}): Promise<UserProfile> {
  const form = new FormData();
  if (params.memberName !== undefined) form.append('memberName', params.memberName);
  if (params.mobile !== undefined) form.append('mobile', params.mobile);
  const res = await apiClient.patch(`${BASE}/users/me`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data as UserProfile;
}

// ── 업체정보 수정 (마스터 전용) ───────────────────────────────────────────────

export async function updateCompanyInfo(params: {
  compName?: string;
  phone?: string;
  email?: string;
  address?: string;
  addressDtl?: string;
  /** address 와 함께 보낸다 — 주소 검색 결과 좌표(없으면 null) */
  gpsLat?: number | null;
  gpsLng?: number | null;
}): Promise<void> {
  await apiClient.patch(`${BASE}/company`, params);
}

// ── 비밀번호 변경 ─────────────────────────────────────────────────────────────

export async function changePassword(params: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  await apiClient.post(`${BASE}/users/me/password`, params);
}

// ── 탈퇴 사전 확인 ────────────────────────────────────────────────────────────

export async function fetchWithdrawalCheck(): Promise<WithdrawalCheck> {
  const res = await apiClient.get(`${BASE}/users/withdrawal-check`);
  return res.data.data as WithdrawalCheck;
}

// ── 탈퇴 실행 ─────────────────────────────────────────────────────────────────

export async function withdrawUser(reason?: string): Promise<void> {
  await apiClient.delete(`${BASE}/users/me`, { data: { reason: reason ?? null } });
}

// ── 비밀번호 본인확인 ─────────────────────────────────────────────────────────

export async function verifyPassword(password: string): Promise<boolean> {
  const res = await apiClient.post(`${BASE}/auth/verify-password`, { password });
  return (res.data.data as { verified: boolean }).verified;
}

// ── 휴대폰 OTP 발송 ───────────────────────────────────────────────────────────

export async function requestPhoneOtp(mobile: string): Promise<void> {
  await apiClient.post(`${BASE}/auth/phone-verify/request`, { mobile });
}

// ── 휴대폰 OTP 인증 ───────────────────────────────────────────────────────────

export async function verifyPhoneOtp(params: {
  mobile: string;
  verifyCode: string;
}): Promise<{ verified: boolean; verifyToken: string | null }> {
  const res = await apiClient.post(`${BASE}/auth/phone-verify/verify`, params);
  return res.data.data as { verified: boolean; verifyToken: string | null };
}
